// 商品元件
import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import dayjs from '@/common/dayjs';
import { DEFAULT_HEAD } from '@/common/constant';

interface Props {
    item: any;
}

const LOTTERY_LABEL = {
    stockPrefix: '商品',
    button: '參加抽獎',
    ended: '活動結束',
} as const;

const REDEEM_LABEL = {
    stockPrefix: '剩餘',
    button: '我要兌換',
    ended: '兌換完畢',
} as const;

const RedemptionItemComponent = ({ item = {} }: Props) => {
    const { name, beginTime, endTime, code, type, coverImg, winnerCount = 0 } = item || {};

    // 抽獎商品、活動結束
    const isLottery = type === 4;

    const remainingStock = item?.remainingStock ?? (isLottery ? 0 : undefined);
    const stardustCost = item?.stardustCost ?? 0;
    // 剩餘數量顯示：抽獎類型 null 當作 0，兌換類型 null 代表無庫存限制，超過 999 顯示無限符號
    const remainingStockText =
        !isLottery && (remainingStock == null || remainingStock > 999)
            ? '∞'
            : isLottery
            ? winnerCount
            : remainingStock;

    // 商品圖片
    const [imgSrc, setImgSrc] = useState(coverImg || DEFAULT_HEAD);

    const isOutOfStock = isLottery ? !winnerCount : remainingStock === 0;
    // 尚未開始
    const isNotStarted = beginTime && dayjs().isBefore(beginTime);
    // 已結束：純粹以結束時間判斷
    const isEnd = dayjs().isAfter(endTime);
    const isTerminal = isEnd || isOutOfStock;

    // 按鈕文字固定顯示「我要兌換」/「我要抽獎」，詳細狀態改以圖片遮罩呈現
    const labels = isLottery ? LOTTERY_LABEL : REDEEM_LABEL;
    const overlayText = isNotStarted ? '即將開始' : isTerminal ? labels.ended : null;
    const isDisabled = isNotStarted || isTerminal;

    return (
        <Link
            href={`/items/${code}`}
            className="bg-white rounded-lg overflow-hidden group xxs:flex"
        >
            <div className="relative aspect-square overflow-hidden shrink-0 w-full xxs:w-38.5 lg:w-52.5">
                {/* 商品圖 */}
                <Image
                    src={imgSrc} // 修改
                    fill
                    sizes="(max-width: 480px) 100vw, (max-width: 1024px) 154px, 210px"
                    className="object-cover group-hover:scale-110 transition-transform duration-300"
                    alt={name}
                    onError={() => setImgSrc(DEFAULT_HEAD)} // 新增
                />

                {/* 詳細狀態遮罩 */}
                {overlayText && (
                    <div className="set-center w-[65%] aspect-square rounded-full bg-[rgba(30,30,30,0.5)] flex items-center justify-center">
                        <span className="text-btn1b text-[#fff]">{overlayText}</span>
                    </div>
                )}
            </div>

            <div className="min-w-0 px-4.5 py-2.5 flex flex-col justify-between flex-1 gap-y-3 lg:py-4.5">
                <div className="gap-y-1 flex flex-col items-start xxs:justify-between xxs:grow lg:gap-y-2.5 lg:grow-0">
                    {/* 商品名稱 */}
                    <h3 className="text-h6 text-black9 w-full xxs:line-clamp-2 lg:mb-2 lg:text-h5">
                        {name}
                    </h3>

                    {/* 數量 */}
                    <div className="space-y-1.5 lg:mt-0 lg:self-start">
                        <p className="text-btn1 text-black8">
                            {labels.stockPrefix}數量 : {remainingStockText}
                        </p>
                        <p className="text-btn2 text-black4 tracking-[0.9px]">
                            {isLottery ? '活動' : '兌換'}時間 :{' '}
                            {beginTime ? dayjs(beginTime).format('YYYY.MM.DD') : '不限'}~
                            {endTime ? dayjs(endTime).format('YYYY.MM.DD') : '不限'}
                        </p>
                    </div>
                </div>

                <div className="flex justify-between items-center text-btn4 text-black9 lg:mt-auto">
                    {/* 星塵消耗 */}
                    <p className="opacity-80">
                        <span className="text-vPurple">{stardustCost.toLocaleString()}</span> 星塵
                    </p>

                    {/* 按鈕 */}
                    <span
                        className={`text-[#fff] text-btn1 py-2 px-5.5 rounded-full hidden lg:block ${
                            isDisabled
                                ? 'bg-black4'
                                : `transition-colors duration-200 ease-in hover:bg-purple ${
                                      isLottery ? 'bg-pale' : 'bg-vPurple'
                                  }`
                        }`}
                    >
                        {labels.button}
                    </span>
                </div>
            </div>
        </Link>
    );
};

export default RedemptionItemComponent;
